/**
 * Emergent Google Auth integration for Boardroom.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. Redirect target is derived dynamically from
 * `window.location.origin` at call time.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { getUserId, resetIdentity } from "./user";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If the URL is carrying a fresh session_id fragment, defer to AuthCallback —
    // running checkAuth() here would 401 before AuthCallback exchanges the token.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      // 2s cap — if the server is slow/hanging, still tear down local state
      // immediately. The server-side session will expire on its own regardless.
      await Promise.race([
        api.post("/auth/logout"),
        new Promise((_, reject) => setTimeout(() => reject(new Error("logout timeout")), 2000)),
      ]);
    } catch (e) {
      console.debug("logout request skipped or failed", e);
    }
    // Wipe the anonymous identity so the dashboard/sessions/goals data tied
    // to this user is no longer visible. A fresh UUID will be minted on the
    // next request. Then force a full reload so every React component (which
    // may have cached fetched data) starts from a clean slate.
    resetIdentity();
    setUser(null);
    window.location.replace("/");
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh: checkAuth, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH.
export function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export async function exchangeSessionId(sessionId) {
  // Pass the current anonymous user id so the backend can migrate their data
  // into the authenticated account if a merge is needed.
  const legacyUserId = (() => {
    try {
      return getUserId();
    } catch {
      return null;
    }
  })();
  const r = await api.post(
    "/auth/session",
    { legacy_user_id: legacyUserId },
    { headers: { "X-Session-Id": sessionId } }
  );
  return r.data;
}
